import {FC, useState} from 'react';
import {useCan, usePersona} from 'src/permissions/hooks';
import {Box} from '@mui/material';
import {GridSortItem, GridSortModel} from '@mui/x-data-grid';
import {LocationSortFieldEnum} from 'src/enums/locations';
import {Table} from '../../tables/Table';
import {TableLayout} from '../../tables/TableLayout';
import {LocationsTableActions} from '../LocationsTableActions';
import {columnConfigLocations} from './LocationTable.tableConfig';
import {LocationItemCard} from './LocationItemCard';
import {LocationFilterValue} from '../LocationsTableActions/LocationsTableActions.types';
import {LocationTableProps} from './LocationTable.types';
import {LocationSearchRequestData} from 'src/types/locations';
import {ResponsiveTable} from '../../common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {useDefineIsRealEstateRole} from 'src/app/hooks';

export const LocationTable: FC<LocationTableProps> = ({
                                                          data,
                                                          loading = false,
                                                          size,
                                                          pageNumber,
                                                          handleUpdateTableData,
                                                          handleChangePage,
                                                          handleChangeRowsNumber,
                                                          isCompanyUser = false,
                                                          isDriverUser = false,
                                                          title,
                                                          onAddClick,
                                                          buttonText,
                                                          onlyTitle,
                                                          icon,
                                                          filters,
                                                       flush,
                                                      }) => {
    // Seeded from the filter the page holds, not from empty: a remount is what coming back from a
    // detail page is, and starting at '' meant the next filter change wiped the search that was
    // applied - `updateData` writes `keyword: search`, so an empty local copy cleared the URL.
    const [searchValue, setSearchValue] = useState<string>(filters.keyword ?? '');
    const [sortValue, setSortValue] = useState<GridSortItem>({
        field: '',
        sort: 'asc',
    });
    // Feeds the delete column and the card's remove action.
    const canCreateDeleteLocation = useCan('locations.delete');
    const persona = usePersona();
    const isEmployeeUser = persona.isEmployee;

    const handleSearch = (value: string) => {
        setSearchValue(value);
        handleUpdateTableData(updateData(value, sortValue, filters));
    };

    // The three fields this toolbar controls, merged onto the rest. Location type is the axis the
    // strip's type tiles are on, so choosing one here clears a gap chosen there - the same
    // alternatives-not-combinations rule, applied from the other control.
    const handleFilter = (value: LocationFilterValue) => {
        handleUpdateTableData(updateData(searchValue, sortValue, {
            ...filters,
            country: value.country,
            city: value.city,
            locationType: value.locationType,
            ...(value.locationType ? {withoutChargers: undefined, withoutPrice: undefined} : {}),
        }));
    };

    const handleSort = (value: GridSortModel) => {
        setSortValue(value[0]);
        handleUpdateTableData(updateData(searchValue, value[0], filters));
    };

    /** A merge onto the filter the list is on, not a fresh object built from the toolbar's fields. */
    const updateData = (
        search: string,
        sort: GridSortItem,
        filter: LocationSearchRequestData
    ): LocationSearchRequestData => ({
        ...filter,
        keyword: search,
        sortAsc: sort ? sort.sort === 'asc' : true,
        sortField: (sort && sort.field) || LocationSortFieldEnum.ID,
    });

    return (
        <Box>
            <TableLayout
                flush={flush}
                // Every reader gets the toolbar. A driver used to get none - no title, no search, no
                // filters - although the server already narrows the list to their own locations.
                toolbar={!flush && (
                    <LocationsTableActions
                        title={title}
                        icon={icon}
                        onAdd={onlyTitle ? undefined : onAddClick}
                        addLabel={buttonText}
                        handleSearch={handleSearch}
                        handleFilter={handleFilter}
                        filters={filters}
                        totalElements={data.page.totalElements}
                    />
                )}
                totalPages={data.page.totalPages}
                page={pageNumber && pageNumber === 0 ? pageNumber : data.page.number}
                pageSize={size}
                onPageChange={handleChangePage}
                onPageSizeChange={handleChangeRowsNumber}
            >
            <ResponsiveTable
                desktopElement={
                    <Table
                        rows={data.content}
                        // In the table's own space rather than over the page: the counts above it come
                        // from a different request and are worth reading while this one arrives.
                        loading={loading}
                        hideFooter
                        columns={columnConfigLocations(canCreateDeleteLocation, isCompanyUser, isEmployeeUser, isDriverUser)}
                        handleSort={handleSort}
                        sx={{border: 'none', borderRadius: 0}}
                    />
                }
                responsiveElement={
                    <CardList>
                        {data.content.map((item) => (
                            <LocationItemCard
                                key={item?.id}
                                canCreateDeleteLocation={canCreateDeleteLocation}
                                {...item}
                            />
                        ))}
                    </CardList>
                }
            />
            </TableLayout>
        </Box>
    );
};
